import { Typography } from '@ui/components'
import clsx from 'clsx'
import type {
  ChangeEvent,
  ChangeEventHandler,
  DetailedHTMLProps,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from 'react'
import { forwardRef, useCallback, useEffect, useRef } from 'react'
import s from './Input.module.scss'

type HTMLProps<TElement extends HTMLElement> = DetailedHTMLProps<HTMLAttributes<TElement>, TElement>
type HTMLInputProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
type WithoutClassName<T extends HTMLProps<any>> = Omit<T, 'className'>

export type InputChangeHandler = (newValue: string, event: ChangeEvent<HTMLInputElement>) => void

/**
 * Input's props inherit HTML Input Element props (html attributes)
 */
export interface InputProps extends Omit<HTMLInputProps, 'value' | 'onChange'> {
  label?: string
  boxShadow?: boolean

  value?: string
  onChange?: InputChangeHandler

  containerClassName?: string
  wrapperClassName?: string

  containerProps?: WithoutClassName<HTMLProps<HTMLDivElement>>
  wrapperProps?: WithoutClassName<HTMLProps<HTMLDivElement>>

  startAdornment?: ReactNode
  endAdornment?: ReactNode
  optional?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    className,
    label,
    children,
    startAdornment,
    endAdornment,
    boxShadow = true,
    containerClassName,
    wrapperClassName,
    containerProps,
    wrapperProps,
    value = '',
    onChange,
    optional = false,
    ...restProps
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const mirrorRef = useRef<HTMLSpanElement>(null)

  const handleInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      onChange?.(event.target.value, event)
    },
    [onChange]
  )

  useEffect(() => {
    if (mirrorRef.current && inputRef.current) {
      mirrorRef.current.textContent = value || ' '
      const mirrorWidth = mirrorRef.current.offsetWidth
      inputRef.current.style.width = `${mirrorWidth + 8}px`
    }
  }, [value])

  return (
    <div className={clsx('flex', containerClassName ? containerClassName : 'flex-col')} {...(containerProps || {})}>
      <div className={clsx('flex justify-between', label ? 'flex' : 'hidden')}>
        {label && (
          <Typography as="span" variant="body" className={s.label}>
            {label}
          </Typography>
        )}
        {optional && (
          <Typography as="span" variant="body" className={clsx(s.label, 'text-bermuda')}>
            {'Optional'}
          </Typography>
        )}
      </div>
      <div
        className={clsx(s['input-wrapper'], boxShadow && s['box-shadow'], wrapperClassName)}
        {...(wrapperProps || {})}
      >
        {startAdornment && <div className="flex">{startAdornment}</div>}
        <span
          ref={mirrorRef}
          className="invisible absolute whitespace-pre pointer-events-none"
          style={{
            position: 'absolute',
            visibility: 'hidden',
            whiteSpace: 'pre',
            fontSize: '24px',
            fontFamily: 'inherit',
            padding: '0 12px',
          }}
        />
        <input
          ref={(el) => {
            if (el) {
              // @ts-ignore
              inputRef.current = el
            }
            if (typeof ref === 'function') ref(el)
            else if (ref) ref.current = el
          }}
          className={clsx(
            s.input,
            'focus:outline-none bg-transparent font-medium text-sm leading-5 h-[32px]',
            className
          )}
          spellCheck={false}
          onChange={handleInputChange}
          value={value}
          {...restProps}
          style={{ width: 'auto' }}
        />
        {endAdornment && <div className="flex">{endAdornment}</div>}
      </div>
      {children}
    </div>
  )
})

Input.displayName = 'Input'

export default Input
